/* eslint-disable no-param-reassign */
import Vue from 'vue';
import { cloneObject } from '@/helpers/utility';
import { findMatchingStationByLocation } from '@/helpers/map';

export default {
  namespaced: true,

  state: {
    cachedRoutes: {},
    currentRouteId: null,
    lastCacheUpdate: null,
    selectedShift: null, // Stores the currently selected shift for edit mode
    shouldSyncStations: false, // Flag to control when station sync should fire
    isMultiShiftCalculating: false, // Flag to disable sync during multi-shift calculation
  },

  mutations: {
    SET_CACHED_ROUTE_DATA(state, { routeId, data }) {
      Vue.set(state.cachedRoutes, routeId, {
        ...data,
        lastUpdated: Date.now(),
      });
      state.lastCacheUpdate = Date.now();
    },

    SET_CURRENT_ROUTE_ID(state, routeId) {
      state.currentRouteId = routeId;
    },

    SET_SELECTED_SHIFT(state, shift) {
      state.selectedShift = shift;
    },

    SET_SHOULD_SYNC_STATIONS(state, shouldSync) {
      state.shouldSyncStations = shouldSync;
    },

    SET_MULTI_SHIFT_CALCULATING(state, isCalculating) {
      state.isMultiShiftCalculating = isCalculating;
    },

    UPDATE_CACHED_WORKGROUP_DATA(state, { routeId, workgroupData }) {
      if (state.cachedRoutes[routeId]) {
        Vue.set(state.cachedRoutes[routeId], 'workgroupData', workgroupData);
        Vue.set(state.cachedRoutes[routeId], 'lastUpdated', Date.now());
      }
    },

    UPDATE_CACHED_DRAFT_VERSIONS(state, { routeId, draftVersions }) {
      if (state.cachedRoutes[routeId]) {
        Vue.set(state.cachedRoutes[routeId], 'draftVersions', draftVersions);
        Vue.set(state.cachedRoutes[routeId], 'lastUpdated', Date.now());
      }
    },

    UPDATE_CACHED_STATIONS(state, { routeId, stations }) {
      if (state.cachedRoutes[routeId]) {
        Vue.set(state.cachedRoutes[routeId], 'stations', stations);
        Vue.set(state.cachedRoutes[routeId], 'lastUpdated', Date.now());
      }
    },

    // Simple mutation to update a single route's stations with synced data
    SYNC_STATION_TO_ROUTE(state, { targetRouteId, syncedStations, timestamp }) {
      if (state.cachedRoutes[targetRouteId]) {
        Vue.set(state.cachedRoutes[targetRouteId], 'stations', syncedStations);
        Vue.set(state.cachedRoutes[targetRouteId], 'lastUpdated', timestamp);
      }
    },

    UPDATE_CACHED_PERSONNEL(state, { routeId, personnel }) {
      if (state.cachedRoutes[routeId]) {
        Vue.set(state.cachedRoutes[routeId], 'personnel', personnel);
        Vue.set(state.cachedRoutes[routeId], 'lastUpdated', Date.now());
      }
    },

    REMOVE_CACHED_ROUTE(state, routeId) {
      Vue.delete(state.cachedRoutes, routeId);
      if (state.currentRouteId === routeId) {
        state.currentRouteId = null;
      }
    },

    CLEAR_ALL_CACHE(state) {
      state.cachedRoutes = {};
      state.currentRouteId = null;
      state.lastCacheUpdate = null;
    },

    CLEAR_ROUTE_CACHE_BY_WORKGROUP(state, workgroupInstanceId) {
      Object.keys(state.cachedRoutes).forEach((routeId) => {
        if (state.cachedRoutes[routeId].workgroupInstanceId === workgroupInstanceId) {
          Vue.delete(state.cachedRoutes, routeId);
        }
      });
    },
  },

  actions: {
    async cacheCurrentRouteData({ commit, rootState }, { routeId, workgroupInstanceId }) {
      try {
        // CRITICAL: Deep clone all data to prevent mutations from affecting cached data
        // Gather current data from various store modules
        const workgroupData = cloneObject(rootState.routeList?.currentWorkgroup);

        // CRITICAL: Only cache versions that belong to the route being cached
        // This prevents cache corruption when store contains versions from different routes
        const storedRouteVersion = rootState.routeEdit?.storedRouteVersion;
        const routeVersion = {
          activeVersion:
            storedRouteVersion?.activeVersion?.routeId === routeId
              ? cloneObject(storedRouteVersion.activeVersion)
              : null,
          draftVersion:
            storedRouteVersion?.draftVersion?.routeId === routeId ? cloneObject(storedRouteVersion.draftVersion) : null,
          activeStations:
            storedRouteVersion?.activeVersion?.routeId === routeId
              ? cloneObject(storedRouteVersion.activeStations || [])
              : [],
          draftStations:
            storedRouteVersion?.draftVersion?.routeId === routeId
              ? cloneObject(storedRouteVersion.draftStations || [])
              : [],
        };

        // Cache routeEdit routesData (TO/FROM route data)
        const routesData = {
          TO: cloneObject(rootState.routeEdit?.routesData?.TO || {}),
          FROM: cloneObject(rootState.routeEdit?.routesData?.FROM || {}),
        };

        // Safely cache station data, ensuring arrays and filtering out problematic properties
        const stations = {
          TO: Array.isArray(rootState.stations?.stationList?.TO)
            ? cloneObject(rootState.stations.stationList.TO).map((station) => ({
                ...station,
                // CRITICAL: Always set routeId to the route being cached
                // Do NOT use fallback (station.routeId || routeId) as it can preserve wrong routeId from synced stations
                routeId,
                // Filter out any Google Maps specific properties that might cause issues
                polyline: undefined,
                path: undefined,
              }))
            : [],
          FROM: Array.isArray(rootState.stations?.stationList?.FROM)
            ? cloneObject(rootState.stations.stationList.FROM).map((station) => ({
                ...station,
                // CRITICAL: Always set routeId to the route being cached
                // Do NOT use fallback (station.routeId || routeId) as it can preserve wrong routeId from synced stations
                routeId,
                // Filter out any Google Maps specific properties that might cause issues
                polyline: undefined,
                path: undefined,
              }))
            : [],
        };

        const personnel = cloneObject(rootState.routePersonnel?.instancePersonnel || []);

        const cacheData = {
          workgroupInstanceId,
          workgroupData,
          routeVersion,
          routesData,
          stations,
          personnel,
        };

        commit('SET_CACHED_ROUTE_DATA', { routeId, data: cacheData });
        commit('SET_CURRENT_ROUTE_ID', routeId);

        return true;
      } catch (error) {
        console.error('Error caching route data:', error);
        return false;
      }
    },

    async loadCachedRouteData({ commit, dispatch }, { routeId, workgroupInstanceId }) {
      try {
        const cachedData = this.getters['routeShifts/getCachedRoute'](routeId);
        if (!cachedData) {
          return false;
        }

        // Verify workgroup instance matches
        if (cachedData.workgroupInstanceId !== workgroupInstanceId) {
          return false;
        }

        // Check if data is complete
        if (!this.getters['routeShifts/isCacheDataComplete'](cachedData)) {
          return false;
        }

        // Restore workgroup data
        if (cachedData.workgroupData) {
          commit('SET_CURRENT_WORKGROUP', cachedData.workgroupData, { root: true });
        }

        // Restore personnel data
        if (cachedData.personnel) {
          commit('SET_INSTANCE_PERSONNELS', cachedData.personnel, { root: true });
          commit('routePersonnel/SET_INSTANCE_PERSONNEL', cachedData.personnel, { root: true });
          // Rebuild personnelMap from personnel array
          commit('SET_PERSONNEL_MAP', cachedData.personnel, { root: true });
          commit('routePersonnel/SET_PERSONNEL_MAP', cachedData.personnel, { root: true });
        }

        // Restore route version (draft and active)
        if (cachedData.routeVersion) {
          // CRITICAL VALIDATION: Verify cached routeIds match what we're loading
          const draftRouteId = cachedData.routeVersion.draftVersion?.routeId;
          const activeRouteId = cachedData.routeVersion.activeVersion?.routeId;

          if (draftRouteId && draftRouteId !== routeId) {
            console.error(
              `❌ CACHE CORRUPTION: Cached draftVersion.routeId (${draftRouteId}) doesn't match requested routeId (${routeId})`,
              'This indicates the cache was corrupted during cacheCurrentRouteData.',
              'Skipping cache load - will fetch fresh data instead.',
            );
            return false; // Signal cache load failed
          }

          if (activeRouteId && activeRouteId !== routeId) {
            console.error(
              `❌ CACHE CORRUPTION: Cached activeVersion.routeId (${activeRouteId}) doesn't match requested routeId (${routeId})`,
              'This indicates the cache was corrupted during cacheCurrentRouteData.',
              'Skipping cache load - will fetch fresh data instead.',
            );
            return false; // Signal cache load failed
          }

          // Restore both draft and active versions with their stations (NO routeId overwriting)
          if (cachedData.routeVersion.draftVersion) {
            const draftPayload = {
              draftVersion: cachedData.routeVersion.draftVersion, // Use cached data as-is
              stations: cachedData.routeVersion.draftStations || [],
            };
            commit('SET_STORED_ROUTE_VERSION', { versionType: 'draft', payload: draftPayload }, { root: true });
          }
          if (cachedData.routeVersion.activeVersion) {
            const activePayload = {
              activeVersion: cachedData.routeVersion.activeVersion, // Use cached data as-is
              stations: cachedData.routeVersion.activeStations || [],
            };
            commit('SET_STORED_ROUTE_VERSION', { versionType: 'active', payload: activePayload }, { root: true });
          }
        } else if (cachedData.draftVersions) {
          // BACKWARD COMPATIBILITY: Handle old cache format with draftVersions
          const draftRouteId = cachedData.draftVersions.draftVersion?.routeId;

          if (draftRouteId && draftRouteId !== routeId) {
            console.error(
              `❌ CACHE CORRUPTION: [Legacy] Cached draftVersion.routeId (${draftRouteId}) doesn't match requested routeId (${routeId})`,
            );
            return false;
          }

          const draftPayload = {
            draftVersion: cachedData.draftVersions.draftVersion, // Use cached data as-is
            stations: cachedData.draftVersions.stations || [],
          };
          commit('SET_STORED_ROUTE_VERSION', { versionType: 'draft', payload: draftPayload }, { root: true });
        }

        // Check if routesData and stations are complete
        const { routeVersion, draftVersions, stations, routesData } = cachedData;
        const hasReturnPath =
          routeVersion?.draftVersion?.routeDetail?.returnPath?.lstRouteStopIds?.length > 0 ||
          draftVersions?.draftVersion?.routeDetail?.returnPath?.lstRouteStopIds?.length > 0;
        const hasCachedFromStations = stations?.FROM?.length > 0;
        const hasCachedFromPath = routesData?.FROM?.path?.length > 0;

        // Check if data is incomplete (missing FROM data when returnPath exists)
        const stationsIncomplete = hasReturnPath && !hasCachedFromStations;
        const routesDataIncomplete = hasReturnPath && !hasCachedFromPath;
        const dataIncomplete = stationsIncomplete || routesDataIncomplete;

        // Restore routeEdit routesData (TO/FROM route data) only if complete
        if (routesData && !routesDataIncomplete) {
          commit('SET_ROUTES_ALL', routesData, { root: true });
        }

        if (dataIncomplete) {
          // Cached data is incomplete, regenerate from draft version
          console.warn(`Cached data incomplete for route ${routeId}:`, {
            stationsIncomplete,
            routesDataIncomplete,
          });

          // Regenerate routesData from draft version if incomplete
          if (routesDataIncomplete) {
            await dispatch('prepareRouteData', null, { root: true });
          }

          // Regenerate stations from draft version if incomplete
          if (stationsIncomplete) {
            await dispatch('prepareStopData', null, { root: true });
          }

          commit('SET_CURRENT_ROUTE_ID', routeId);
          return true;
        }

        if (stations) {
          // Restore stations from cache
          // Ensure TO stations is always an array
          const toStations = Array.isArray(stations.TO) ? stations.TO : [];
          commit('SET_STATION_LIST', { route: 'TO', payload: toStations }, { root: true });

          // Ensure FROM stations is always an array
          const fromStations = Array.isArray(stations.FROM) ? stations.FROM : [];
          commit('SET_STATION_LIST', { route: 'FROM', payload: fromStations }, { root: true });

          commit('SET_CURRENT_ROUTE_ID', routeId);
          return true;
        }

        // If we reach here, stations data was missing
        commit('SET_CURRENT_ROUTE_ID', routeId);
        return true;
      } catch (error) {
        console.error('Error loading cached route data:', error);
        return false;
      }
    },

    clearRouteCache({ commit }, routeId) {
      commit('REMOVE_CACHED_ROUTE', routeId);
    },

    clearAllCache({ commit }) {
      commit('CLEAR_ALL_CACHE');
    },

    clearCacheByWorkgroup({ commit }, workgroupInstanceId) {
      commit('CLEAR_ROUTE_CACHE_BY_WORKGROUP', workgroupInstanceId);
    },

    // Cache invalidation triggers
    onRouteSave({ dispatch }, routeId) {
      dispatch('clearRouteCache', routeId);
    },

    onRouteUpdate({ dispatch }, routeId) {
      dispatch('clearRouteCache', routeId);
    },

    onNavigateAway({ dispatch, state }) {
      if (state.currentRouteId) {
        dispatch('cacheCurrentRouteData', {
          routeId: state.currentRouteId,
          workgroupInstanceId: state.cachedRoutes[state.currentRouteId]?.workgroupInstanceId,
        });
      }
    },

    onManualRefresh({ dispatch }, routeId) {
      dispatch('clearRouteCache', routeId);
    },

    // Fetch fresh data for a route shift (used when cache is unavailable or invalid)
    async fetchFreshDataForShift({ dispatch, commit }, { routeId, workgroupInstanceId }) {
      if (!routeId || !workgroupInstanceId) {
        console.warn('fetchFreshDataForShift: Missing routeId or workgroupInstanceId');
        return false;
      }

      try {
        // Reset state for clean data loading
        dispatch('resetStationList', null, { root: true });
        commit('RESET_ROUTES', null, { root: true });
        dispatch('routePersonnel/resetPersonnelData', null, { root: true });

        // Set current route ID
        commit('SET_CURRENT_ROUTE_ID', routeId);

        // Fetch workgroup and route data
        await dispatch('fetchWorkgroupData', workgroupInstanceId, { root: true });
        await dispatch('fetchRouteVersion', { routeId }, { root: true });

        return true;
      } catch (error) {
        console.error('Error fetching fresh data for shift:', error);
        return false;
      }
    },

    // Update current cached stations and sync across all cached routes
    updateAndSyncStations({ commit, state, rootState, rootGetters }, { routeId }) {
      // Reset the flag immediately to prevent multiple syncs
      commit('SET_SHOULD_SYNC_STATIONS', false);

      // CRITICAL: Skip sync during multi-shift calculation
      // Each route will be calculated separately and cached individually
      // Syncing during calculation would corrupt timing data
      if (state.isMultiShiftCalculating) {
        return;
      }

      // Only sync if there are multiple shifts
      const routeShifts = rootGetters.routeShifts || [];
      if (routeShifts.length <= 1) return;

      const sourceRoute = state.cachedRoutes[routeId];
      if (!sourceRoute) return;

      // CRITICAL: Get source stations from the ACTIVE stations module, not from cache
      // The cache might have stale data if the station was just updated
      const activeStations = rootState.stations?.stationList;
      if (!activeStations) return;

      // CRITICAL: Deep clone source stations to prevent mutations
      const sourceStations = cloneObject(activeStations);

      const currentTimestamp = Date.now();

      // Iterate over all other cached routes and sync shared station properties
      Object.keys(state.cachedRoutes).forEach((targetRouteId) => {
        if (targetRouteId === routeId) return; // Skip source route

        const targetRoute = state.cachedRoutes[targetRouteId];
        if (!targetRoute) return;

        // CRITICAL: Deep clone target stations to prevent mutations
        const targetStations = cloneObject(targetRoute.stations || { TO: [], FROM: [] });

        // Synchronize each direction (TO, FROM)
        const syncedStations = {};

        Object.keys(sourceStations).forEach((direction) => {
          if (!Array.isArray(sourceStations[direction])) {
            syncedStations[direction] = cloneObject(sourceStations[direction]);
            return;
          }

          // Start with empty array, will add synced stations
          syncedStations[direction] = [];

          // First: Update existing stations and add new ones from source
          sourceStations[direction].forEach((sourceStation) => {
            // CRITICAL FIX: Match stations by LOCATION instead of ID
            // Each route shift has unique station IDs, but they share the same physical locations
            const existingStation = findMatchingStationByLocation(sourceStation, targetStations[direction]);

            if (existingStation) {
              // Station exists at same location - sync shared properties, keep route-specific data
              syncedStations[direction].push({
                ...cloneObject(existingStation), // Keep ALL existing route-specific data (timing, personnel, etc.)
                // Sync ONLY shared properties from source station:
                // NOTE: Keep existingStation.id, NOT sourceStation.id (IDs are route-specific)
                name: sourceStation.name,
                description: sourceStation.description,
                location: cloneObject(sourceStation.location),
                isWorkLocation: sourceStation.isWorkLocation,
                isPassthrough: sourceStation.isPassthrough ?? false,
                // NOTE: Keep existingStation.counterStopId (also route-specific)
                // CRITICAL: Ensure routeId stays as the target route
                routeId: targetRouteId,
              });
            } else {
              // Station doesn't exist in target route at this location - this is a NEW station
              // Create with source properties but mark it needs its own timing calculation
              syncedStations[direction].push({
                id: sourceStation.id, // Use source ID for new stations
                name: sourceStation.name,
                description: sourceStation.description,
                location: cloneObject(sourceStation.location),
                isWorkLocation: sourceStation.isWorkLocation,
                isPassthrough: sourceStation.isPassthrough ?? false,
                counterStopId: sourceStation.counterStopId,
                routeId: targetRouteId,
                personnels: [],
                personnelIds: [],
                // New stations won't have timing - they need to be calculated for this route
              });
            }
          });

          // Stations that exist in target but NOT in source have been deleted
          // They are intentionally NOT included in syncedStations - this handles deletion sync
        });

        // Commit the synced stations to the target route
        commit('SYNC_STATION_TO_ROUTE', {
          targetRouteId,
          syncedStations,
          timestamp: currentTimestamp,
        });
      });

      // Update the source route's timestamp
      commit('UPDATE_CACHED_STATIONS', {
        routeId,
        stations: sourceStations,
      });
    },

    // Save all route shifts sequentially
    async saveAllShifts({ dispatch, commit, state, rootState }, { shifts, options = {} }) {
      const { activate = false } = options;
      const originalRouteId = state.currentRouteId;
      const errors = [];
      const savedShifts = [];

      try {
        /* eslint-disable no-await-in-loop */
        for (let i = 0; i < shifts.length; i += 1) {
          const shift = shifts[i];
          const { routeId, workgroupInstanceId } = shift;

          try {
            // Get the actual current routeId from store (updates each iteration)
            const { currentRouteId } = state;

            // Cache current route before switching
            if (routeId !== currentRouteId) {
              await dispatch('cacheCurrentRouteData', {
                routeId: currentRouteId,
                workgroupInstanceId,
              });

              // Load the shift's data
              await dispatch('loadCachedRouteData', {
                routeId,
                workgroupInstanceId,
              });
            }

            // CRITICAL: Verify the store has the correct route's data after loading from cache
            // If the cached routeVersion was null/empty, the store still has the previous route's data
            // This would cause saveRouteDetails to save the wrong route
            const storedRouteVersion = rootState.routeEdit?.storedRouteVersion;
            const storedDraftRouteId = storedRouteVersion?.draftVersion?.routeId;

            if (storedDraftRouteId !== routeId) {
              console.warn(
                `⚠️ Store has wrong route data after cache load. Expected: ${routeId}, Got: ${storedDraftRouteId}. Fetching fresh data...`,
              );

              // Save cached data before fetching (fetchRouteVersion will overwrite them)
              const cachedData = this.getters['routeShifts/getCachedRoute'](routeId);
              const { stations: cachedStations, routesData: cachedRoutesData } = cachedData || {};

              // Fetch fresh DRAFT version data for this route
              // CRITICAL: Must explicitly pass fetchActive: false to get draft version
              // Otherwise it uses currentSource from state which may be 'active' after previous activation
              await dispatch('fetchRouteVersion', { routeId, fetchActive: false }, { root: true });

              // Restore cached routesData (path/polyline info)
              // fetchRouteVersion calls prepareRouteData which overwrites with empty backend data
              if (cachedRoutesData) {
                commit('SET_ROUTES_ALL', cachedRoutesData, { root: true });
              }

              // Restore cached stations with timing data
              if (cachedStations && typeof cachedStations === 'object') {
                const cachedToStations = Array.isArray(cachedStations.TO) ? cachedStations.TO : [];
                const cachedFromStations = Array.isArray(cachedStations.FROM) ? cachedStations.FROM : [];

                commit('SET_STATION_LIST', { route: 'TO', payload: cachedToStations }, { root: true });
                commit('SET_STATION_LIST', { route: 'FROM', payload: cachedFromStations }, { root: true });
              }
            }

            // Save this shift
            await dispatch('saveRouteDetails', null, { root: true });

            // Activate if requested
            if (activate) {
              await dispatch('activateRouteDetails', null, { root: true });
            }

            // Fetch fresh route version after save/activation
            // After activation, backend creates a NEW draft with a NEW ID
            try {
              await dispatch('fetchRouteVersion', { routeId }, { root: true });
            } catch (fetchError) {
              console.error(`Error fetching route version for routeId ${routeId}:`, fetchError);
            }

            savedShifts.push({ routeId, success: true });
          } catch (shiftError) {
            console.error(`Error ${activate ? 'saving and activating' : 'saving'} shift ${routeId}:`, shiftError);
            errors.push({
              routeId,
              error: shiftError,
              message: shiftError?.response?.data?.error?.message || shiftError.message,
            });
          }
        }
        /* eslint-enable no-await-in-loop */

        // Reload the original shift after all operations
        if (originalRouteId) {
          const originalShift = shifts.find((s) => s.routeId === originalRouteId);
          if (originalShift) {
            await dispatch('loadCachedRouteData', {
              routeId: originalShift.routeId,
              workgroupInstanceId: originalShift.workgroupInstanceId,
            });
          }
        }

        return {
          success: errors.length === 0,
          savedShifts,
          errors,
          totalShifts: shifts.length,
          successCount: savedShifts.length,
          errorCount: errors.length,
        };
      } catch (error) {
        console.error(`Error in saveAllShifts:`, error);
        return {
          success: false,
          savedShifts,
          errors: [...errors, { error, message: error.message }],
          totalShifts: shifts.length,
          successCount: savedShifts.length,
          errorCount: errors.length + 1,
        };
      }
    },

    // Create cached data for all shifts when multiple shifts are available
    async createCachedDataForAllShifts({ dispatch, commit, state, rootGetters }) {
      // CRITICAL: Prevent re-entry if caching is already in progress
      // This can happen if multiple routeShiftSelector instances exist (view + edit mode)
      if (state.isMultiShiftCalculating) {
        return;
      }

      // Set loading state
      dispatch('setRouteEditLoading', true, { root: true });

      // CRITICAL: Disable station sync during cache creation
      // Without this, the shouldSyncStations watcher can fire and contaminate
      // routesData and stations between routes, causing all routes to have the same data
      commit('SET_MULTI_SHIFT_CALCULATING', true);

      try {
        // Get shifts from store instead of parameter
        const shifts = rootGetters.routeShifts;
        if (!shifts || shifts.length <= 1) return;

        // Process each shift sequentially to avoid conflicts
        const cacheShift = async (shift) => {
          const { routeId, workgroupInstanceId } = shift;

          try {
            // Reset state for clean data loading
            dispatch('resetStationList', null, { root: true });
            commit('RESET_ROUTES', null, { root: true });
            dispatch('routePersonnel/resetPersonnelData', null, { root: true });

            // Set current route ID BEFORE fetching data so prepareStopData uses correct routeId
            commit('SET_CURRENT_ROUTE_ID', routeId);

            // Fetch workgroup and route data for this shift
            await dispatch('fetchWorkgroupData', workgroupInstanceId, { root: true });

            // Fetch route version - fetchActive: null will auto-detect from currentSource
            // Now that cache mutation is fixed with deep cloning, we only need the appropriate version
            await dispatch('fetchRouteVersion', { routeId }, { root: true });

            // Cache the loaded data
            await dispatch('cacheCurrentRouteData', {
              routeId,
              workgroupInstanceId,
            });
          } catch (error) {
            console.error(`Error caching data for shift ${routeId}:`, error);
            // Silently handle errors to avoid breaking the cache process for other shifts
          }
        };

        // Process shifts sequentially
        /* eslint-disable no-await-in-loop */
        for (let i = 0; i < shifts.length; i += 1) {
          await cacheShift(shifts[i]);
        }
        /* eslint-enable no-await-in-loop */

        // Note: State restoration is handled by the calling component
        // since it knows which shift was originally selected
      } catch (error) {
        // Handle any errors during cache creation
        console.error('Error in createCachedDataForAllShifts:', error);
      } finally {
        // CRITICAL: Always reset the multi-shift calculating flag
        // This allows station sync to resume after caching is complete
        commit('SET_MULTI_SHIFT_CALCULATING', false);

        // Always clear loading state
        dispatch('setRouteEditLoading', false, { root: true });
      }
    },
  },

  getters: {
    getCachedRoute: (state) => (routeId) => state.cachedRoutes[routeId] || null,

    hasCachedRoute: (state) => (routeId) => !!state.cachedRoutes[routeId],

    shouldSyncStations: (state) => state.shouldSyncStations,

    isCacheDataComplete: () => (cachedData) => {
      if (!cachedData) return false;

      // Check if essential data exists
      const hasWorkgroupData = cachedData.workgroupData;
      const hasRoutesData = cachedData.routesData;
      const hasStations = cachedData.stations;

      // CRITICAL: draftVersions is optional - cache is still valid without it
      // The system can regenerate route data from stations and workgroup data if needed
      // This allows cached route shifts to be loaded even when draftVersions wasn't available during caching
      return hasWorkgroupData && hasRoutesData && hasStations;
    },

    getCacheAge: (state) => (routeId) => {
      const cached = state.cachedRoutes[routeId];
      if (!cached) return null;
      return Date.now() - cached.lastUpdated;
    },

    getAllCachedRouteIds: (state) => Object.keys(state.cachedRoutes),

    getCacheStats: (state) => ({
      routeCount: Object.keys(state.cachedRoutes).length,
      totalSize: JSON.stringify(state.cachedRoutes).length,
      lastUpdate: state.lastCacheUpdate,
    }),

    currentRouteId: (state) => state.currentRouteId,

    getCurrentCachedRoute: (state) => (state.currentRouteId ? state.cachedRoutes[state.currentRouteId] : null),

    selectedShift: (state) => state.selectedShift,
  },
};

/* eslint-enable no-param-reassign */
